import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { patientKeys } from './query-key-factory'

import { MutationConfig } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'
import { Gender } from '@/types/user'

export const updatePatientInputSchema = z.object({
  name: z.string().min(1, 'Le titre est requis').max(70, 'Le titre doit contenir au plus 70 caractères'),
  age: z.coerce.number().min(1, 'Requis'),
  gender: z.custom<Gender>(),
})

export type UpdatePatientInput = z.infer<typeof updatePatientInputSchema>

export const updatePatient = async ({ pid, values }: { pid: string; values: UpdatePatientInput }) => {
  const { error, status, statusText } = await supabase
    .from('patients')
    .update({
      name: values.name,
      age: values.age,
      gender: values.gender,
    })
    .eq('id', pid)
    .is('deleted_at', null)

  if (error) {
    logger.error('Error updating patient', {
      errorMessage: error,
      pid,
      values,
    })
    throw error
  }

  if (status < 200 || status >= 300) {
    logger.error('Error updating patient', {
      status,
      statusText,
      pid,
      values,
    })
    throw new Error(statusText)
  }

  return { status }
}

type UseUpdatePatientOptions = {
  pid: string
  mutationConfig?: MutationConfig<typeof updatePatient>
}

export const useUpdatePatient = ({ pid, mutationConfig }: UseUpdatePatientOptions) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restConfig } = mutationConfig || {}

  return useMutation({
    onSuccess: (...args) => {
      queryClient.refetchQueries({
        queryKey: patientKeys.all,
      })
      onSuccess?.(...args)
    },
    mutationFn: ({ values }: { values: UpdatePatientInput }) => updatePatient({ pid, values }),
    ...restConfig,
  })
}
